import { _js } from '@ifixit/utils/localize';
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogTitle,
} from '@ifixit/ui/components/dialog';

/**
 * The editor's keyboard-shortcut reference. Opened from the footer link (any
 * tab) and from Shift+/ on the Steps tab, so its open state lives at the page
 * level rather than inside a single tab.
 *
 * The line shortcuts mirror the bindings in StepEditor's handleLineShortcut,
 * the step/tab shortcuts in StepsTab/GuideEditPage, and the save chord in
 * Shared/editor/use_save_shortcut; keep this list in step with those handlers.
 */
export function KeyboardShortcutsDialog({
   open,
   onOpenChange,
}: {
   open: boolean;
   onOpenChange: (open: boolean) => void;
}) {
   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogTitle className="mb-2">{_js('Keyboard shortcuts')}</DialogTitle>
            <DialogDescription className="sr-only">
               {_js('A reference list of keyboard shortcuts available while editing this guide.')}
            </DialogDescription>
            <ShortcutList
               title={_js('Step lines (while editing a line)')}
               shortcuts={[
                  ['Enter', _js('Save and edit the next line')],
                  ['Shift + Enter', _js('Insert a new line above')],
                  ['Tab', _js('Indent current line')],
                  ['Shift + Tab', _js('Unindent current line')],
                  ['Ctrl + Shift + N', _js('Insert a new line below')],
                  // Ctrl + N (next line) stays bound in StepEditor but is
                  // deliberately left off this reference.
                  ['Ctrl + P', _js('Previous line')],
                  ['Ctrl + ↑', _js('Move current line up')],
                  ['Ctrl + ↓', _js('Move current line down')],
                  ['Ctrl + D', _js('Delete current line')],
                  ['Ctrl + .', _js('Next bullet color (ROYGBV, then the icons)')],
                  ['Ctrl + ,', _js('Previous bullet color')],
               ]}
            />
            <ShortcutList
               title={_js('Steps')}
               shortcuts={[
                  ['Ctrl + Shift + Enter', _js('Add a new step')],
                  ['↓', _js('Navigate to next step')],
                  ['↑', _js('Navigate to previous step')],
                  ['Shift + /', _js('Open this help')],
               ]}
            />
            <ShortcutList
               title={_js('Tabs')}
               shortcuts={[
                  ['→', _js('Next tab')],
                  ['←', _js('Previous tab')],
               ]}
            />
            <ShortcutList
               title={_js('Saving')}
               shortcuts={[['Ctrl + S', _js('Save the current step, or the guide on other tabs')]]}
            />
         </DialogContent>
      </Dialog>
   );
}

export function ShortcutList({
   title,
   shortcuts,
}: {
   title: string;
   shortcuts: [string, string][];
}) {
   return (
      <div>
         <h3 className="mb-2 text-sm font-semibold text-gray-900">{title}</h3>
         <dl className="m-0 text-sm">
            {shortcuts.map(([keys, action]) => (
               // flex-row-reverse keeps the dt → dd source order the dl
               // demands while the design reads description first.
               <div
                  key={keys}
                  className="flex flex-row-reverse items-center justify-between gap-4 py-1"
               >
                  <dt className="flex shrink-0 gap-1">
                     {keys.split(' + ').map(key => (
                        <kbd
                           key={key}
                           className="inline-flex h-6 min-w-6 items-center justify-center rounded-lg border border-gray-300 bg-gray-100 px-1.5 font-mono text-xs font-medium text-gray-700"
                        >
                           {key}
                        </kbd>
                     ))}
                  </dt>
                  <dd className="m-0 text-xs text-gray-700">{action}</dd>
               </div>
            ))}
         </dl>
      </div>
   );
}
